import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { Star, LocateFixed } from "lucide-react";

const COLORS = { doctor: "#0284C7", hospital: "#1E3A8A", clinic: "#0D9488", diagnostic: "#DC2626" };

const pinIcon = (color, active, letter) => {
  const s = active ? 34 : 28;
  return L.divIcon({
    className: "apn-pin",
    html: `<div style="background:${color};width:${s}px;height:${s}px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;">
      <span style="transform:rotate(45deg);color:#fff;font-weight:800;font-size:11px;font-family:sans-serif;">${letter}</span></div>`,
    iconSize: [s, s],
    iconAnchor: [s / 2, s],
    popupAnchor: [0, -s],
  });
};

const userIcon = L.divIcon({
  className: "apn-pin",
  html: `<div class="loc-pulse" style="position:relative;width:16px;height:16px;border-radius:9999px;background:#0EA5E9;border:2px solid #fff;box-shadow:0 0 0 4px rgba(14,165,233,.3)"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

function MapEffects({ items, center, myPos }) {
  const map = useMap();
  useEffect(() => {
    if (myPos) { map.setView([myPos.lat, myPos.lng], 13); return; }
    if (center) { map.setView([center.lat, center.lng], 12); return; }
    if (items.length > 1) {
      map.fitBounds(L.latLngBounds(items.map((i) => [i.lat, i.lng])), { padding: [50, 50] });
    } else if (items.length === 1) {
      map.setView([items[0].lat, items[0].lng], 14);
    }
  }, [map, items, center, myPos]);
  return null;
}

export function MapView({ items = [], center, activeId, onSelect, className = "h-[420px]", userPos, showLocate }) {
  const [myPos, setMyPos] = useState(userPos || null);
  const [locating, setLocating] = useState(false);
  useEffect(() => { if (userPos) setMyPos(userPos); }, [userPos]);

  const defaultCenter = center || (items[0] ? { lat: items[0].lat, lng: items[0].lng } : { lat: 20.5937, lng: 78.9629 });

  const locate = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setMyPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocating(false); },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div className={`${className} rounded-2xl overflow-hidden border border-slate-200 relative`} data-testid="map-view">
      <MapContainer center={[defaultCenter.lat, defaultCenter.lng]} zoom={center ? 12 : 5} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
        <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <MapEffects items={items} center={center} myPos={myPos} />
        {myPos && <Marker position={[myPos.lat, myPos.lng]} icon={userIcon}><Popup>You are here</Popup></Marker>}
        {items.map((p) => (
          <Marker key={p.id} position={[p.lat, p.lng]} icon={pinIcon(COLORS[p.type] || "#06152D", activeId === p.id, (p.type || "H")[0].toUpperCase())}
            eventHandlers={{ click: () => onSelect?.(p.id) }}>
            <Popup>
              <div className="w-52" data-testid={`map-info-${p.id}`}>
                <img src={p.image} alt="" className="h-24 w-full object-cover rounded-lg mb-2" />
                <div className="font-bold text-[#06152D] text-sm leading-tight">{p.name}</div>
                <div className="text-xs text-slate-600 mt-0.5">{p.specialty || (p.specialties || []).slice(0, 2).join(", ")} · {p.area}</div>
                <div className="flex items-center justify-between mt-2">
                  <span className="inline-flex items-center gap-1 text-xs font-bold"><Star size={11} className="fill-amber-400 text-amber-400" />{p.rating}</span>
                  <Link to={`/provider/${p.id}`} className="text-xs font-semibold text-white bg-[#06152D] px-3 py-1 rounded-full">View</Link>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      {showLocate && (
        <button onClick={locate} data-testid="map-use-my-location-button"
          className="absolute top-3 right-3 z-[1000] inline-flex items-center gap-1.5 rounded-full bg-white shadow-md px-3 py-2 text-xs font-semibold text-[#06152D] hover:bg-sky-50 transition-colors">
          <LocateFixed size={14} className={locating ? "animate-spin" : ""} /> {locating ? "Locating…" : "Use My Location"}
        </button>
      )}
      <div className="absolute bottom-3 left-3 z-[1000] flex gap-2 text-[10px] font-semibold pointer-events-none">
        {Object.entries(COLORS).map(([k, c]) => <span key={k} className="inline-flex items-center gap-1 bg-white/90 backdrop-blur px-2 py-1 rounded-full shadow"><span className="h-2 w-2 rounded-full" style={{ background: c }} />{k}</span>)}
      </div>
    </div>
  );
}
